import {
  CHANGE_READINESS_LABELS,
  SCORE_KEYS,
  otherTabFilterLine,
  SCORE_KEY_LABELS_DE,
  WORK_LOCATION_LABELS,
  type CandidateFilters,
  type CandidateTab,
  type ScoreKey,
} from "@vidario/domain";
import { FilterChip } from "@vidario/ui";
import { type ReactElement } from "react";

export interface CandidateFilterChipsProps {
  filters: CandidateFilters;
  onChange: (next: CandidateFilters) => void;
  /** Aussortiert: the score there is the Vorauswahl's (#186). */
  vorauswahl?: boolean;
  /** The whole filter set and the tab, to name the filters that only work
   * on another tab (#201); null hides that line. */
  otherTab?: { filters: CandidateFilters; tab: CandidateTab } | null;
}

interface ChipDef {
  key: string;
  label: string;
  remove: () => void;
}

/** Compact chip strip rendered above the candidate list showing every
 * active filter constraint. Each chip's `×` removes only its own
 * dimension — leaves the rest of the filter set intact. */
export function CandidateFilterChips({
  filters,
  onChange,
  vorauswahl = false,
  otherTab = null,
}: CandidateFilterChipsProps): ReactElement | null {
  const chips: ChipDef[] = [];

  if (typeof filters.minScore === "number" && filters.minScore > 0) {
    chips.push({
      key: "minScore",
      label: vorauswahl
        ? `Vorauswahl ab ${filters.minScore}`
        : `Gesamt-Score ≥ ${filters.minScore}%`,
      remove: () => onChange(omit(filters, "minScore")),
    });
  }

  if (typeof filters.maxDistanceKm === "number") {
    chips.push({
      key: "maxDistance",
      label: `≤ ${filters.maxDistanceKm} km`,
      remove: () => onChange(omit(filters, "maxDistanceKm")),
    });
  }

  if (filters.minSubScores) {
    for (const key of SCORE_KEYS) {
      const v = filters.minSubScores[key];
      if (typeof v !== "number" || v === 0) continue;
      chips.push({
        key: `sub-${key}`,
        label: `${SCORE_KEY_LABELS_DE[key]} ≥ ${v}%`,
        remove: () => removeSubScore(filters, key, onChange),
      });
    }
  }

  if (filters.mustHavesOnly) {
    chips.push({
      key: "mustHaves",
      label: "Nur Must-Have-Treffer",
      remove: () => onChange(omit(filters, "mustHavesOnly")),
    });
  }

  if (typeof filters.salaryMin === "number" || typeof filters.salaryMax === "number") {
    const min = filters.salaryMin ?? "—";
    const max = filters.salaryMax ?? "—";
    chips.push({
      key: "salary",
      label: `Gehalt ${min}–${max} €`,
      remove: () => onChange(omit(filters, "salaryMin", "salaryMax")),
    });
  }

  if (filters.workLocations && filters.workLocations.length > 0) {
    chips.push({
      key: "locations",
      label: `Arbeitsort: ${filters.workLocations.map((l) => WORK_LOCATION_LABELS[l]).join(", ")}`,
      remove: () => onChange(omit(filters, "workLocations")),
    });
  }

  if (filters.changeReadiness && filters.changeReadiness.length > 0) {
    chips.push({
      key: "readiness",
      label: `Wechsel: ${filters.changeReadiness.map((r) => CHANGE_READINESS_LABELS[r]).join(", ")}`,
      remove: () => onChange(omit(filters, "changeReadiness")),
    });
  }

  const otherTabLine =
    otherTab && otherTabFilterLine(otherTab.filters, otherTab.tab, chips.length > 0);
  if (chips.length === 0 && !otherTabLine) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" role="region" aria-label="Aktive Filter">
          {chips.map((chip) => (
            <FilterChip key={chip.key} onRemove={chip.remove}>
              {chip.label}
            </FilterChip>
          ))}
        </div>
      )}
      {otherTabLine && (
        <p className="m-0 basis-full text-xs text-text-tertiary sm:basis-auto">{otherTabLine}</p>
      )}
    </div>
  );
}

function removeSubScore(
  filters: CandidateFilters,
  key: ScoreKey,
  onChange: (next: CandidateFilters) => void,
): void {
  const next = { ...(filters.minSubScores ?? {}) };
  delete next[key];
  if (Object.keys(next).length === 0) {
    onChange(omit(filters, "minSubScores"));
  } else {
    onChange({ ...filters, minSubScores: next });
  }
}

function omit<T extends object, K extends keyof T>(obj: T, ...keys: readonly K[]): Omit<T, K> {
  const result = { ...obj };
  for (const k of keys) delete result[k];
  return result;
}
